// components/ui/popover.tsx

"use client";

import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { cn } from "@/lib/utils";
import { isTourLayerTarget } from "@/lib/tour/layer";
import { composeRefs } from "@/lib/ui/compose-refs";
import { useRegisterLayer } from "@/lib/ui/layer-stack";

const Popover = PopoverPrimitive.Root;

const PopoverTrigger = PopoverPrimitive.Trigger;

const PopoverAnchor = PopoverPrimitive.Anchor;

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = "center", sideOffset = 4, collisionPadding = 8, onInteractOutside, children, ...props }, ref) => {
  // Inscrit dans la pile des couches (cf. lib/ui/layer-stack.ts) ; la visite
  // guidée le referme par son bouton caché, jamais par un faux Échap.
  const [node, setNode] = React.useState<HTMLDivElement | null>(null);
  const closeRef = React.useRef<HTMLButtonElement>(null);
  const refs = React.useMemo(() => composeRefs(ref, (n: HTMLDivElement | null) => setNode(n)), [ref]);
  useRegisterLayer(node, "popover", { modal: false, close: () => closeRef.current?.click() });
  return (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Content
      ref={refs}
      onInteractOutside={(e) => {
        // 🔴 Un clic dans la bulle de la visite guidée n'est pas un clic
        // « dehors » : sans ça, « Suivant » fermerait ce panneau.
        if (isTourLayerTarget(e.target)) {
          e.preventDefault();
          return;
        }
        onInteractOutside?.(e);
      }}
      align={align}
      sideOffset={sideOffset}
      // Sans marge de collision, le panneau peut venir coller le bord exact de
      // la fenêtre : sa bordure et son ombre sont alors rognées, et la hauteur
      // « disponible » annoncée ci-dessous vaut la distance au bord au pixel près.
      collisionPadding={collisionPadding}
      className={cn(
        "z-50 rounded-xl border border-neutral-100 bg-white p-4 text-neutral-900 shadow-lg outline-none",
        // 🔴 Ces deux classes sont ce qui rend un popover ADAPTABLE à l'écran.
        // Radix CALCULE déjà la place disponible et la publie dans
        // `--radix-popover-content-available-height` — mais rien ne la
        // consommait : le panneau se dessinait à sa hauteur naturelle et sortait
        // de la fenêtre, sans barre de défilement puisqu'il est portalisé.
        // Mesuré le 2026-08-14 sur le sélecteur de date à 713×551 : panneau de
        // 538 px pour 421 px annoncés disponibles → `top: -116px`, donc
        // l'en-tête mois/année ET la ligne des jours de la semaine étaient
        // AU-DESSUS du bord supérieur de l'écran, et rien ne pouvait les
        // ramener. Un appelant qui impose son propre `max-h-*` reste
        // prioritaire (`cn` = twMerge), c'est voulu.
        "max-h-[var(--radix-popover-content-available-height)] overflow-y-auto overscroll-contain",
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
        "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
        "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className
      )}
      {...props}
    >
      {children}
      <PopoverPrimitive.Close ref={closeRef} className="hidden" tabIndex={-1} aria-hidden />
    </PopoverPrimitive.Content>
  </PopoverPrimitive.Portal>
  );
});
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor };